import React, { FunctionComponent, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers/index";
import {
  CartTotalSummaryContainer,
  CartTotalSummaryBody,
  CartTotalSummaryDetail,
  CartSummaryModalMobile,
  CartTotalSummaryTitle,
  ContainerTwoStep,
  CartTotalSummaryTitleContainer,
  CartTotalSummaryAmount,
  CartDetailSummaryContainer,
  DiscountCodeInputContainer,
  DiscountCodeInputField,
  DiscountCodeContainer,
  DiscountCodeLabelContainer,
  DiscountCodeButton,
  DiscountCodeErrorText,
  DiscountCouponsContainer,
  LoadContainer,
} from "../style";
import CurrencyFormat from "../../../utils/currencyFormat";
import { setFooterBottomSpacing } from "../../../redux/actions";
import CartProductSummary from "../CartProductSummary";
import { viewTotalDiscountAmount } from "../../../utils/formatData";
import { Button, withStyles } from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import { styled } from "@mui/system";
import { Label } from "@material-ui/icons";
import Spinner from "../../Spinner";
import { language } from "../../../utils/language";
import { useDiscountCoupons } from "../../../hooks/useDiscountCoupons";
import { isFreeShippingCoupon } from "../../../utils/discountTypes";

type CartTotalSummaryTwoProps = {
  data: any[];
  publicKey: string;
  clientId: number;
  totalAmount: number;
  shippingAmount: number;
  taxAmountTotal: number;
  subTotalAmount: number;
  consumptionTaxAmountTotal: number;
  cartId: string;
  catalogueId: string;
  color: string;
  isValidForm: boolean;
  loading: boolean;
  successInfo: boolean;
  appliedCoupons: Coupon[];
  items: any[];
  setAppliedCoupons: any;
  nextStep?: () => any;
  getFormValues?: () => any;
  showDetailOnMobile: boolean;
  freeShippingReached?: boolean;
  freeShippingNotice?: React.ReactNode;
};

const CartTotalSummary: FunctionComponent<CartTotalSummaryTwoProps> = ({
  cartId,
  catalogueId,
  data,
  color,
  taxAmountTotal,
  consumptionTaxAmountTotal,
  loading,
  appliedCoupons,
  setAppliedCoupons,
  showDetailOnMobile,
  subTotalAmount,
  items,
  freeShippingReached = false,
  freeShippingNotice,
}) => {
  const dispatch = useDispatch();
  const homeState: HomeStateType = useSelector(
    (state: RootState) => state.home
  );
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const {
    couponCode,
    showCouponError,
    couponErrorMessages,
    loadCode,
    handleInputChange,
    handleApplyCoupon,
    handleCloseCoupon,
  } = useDiscountCoupons({
    cartId,
    catalogueId,
    items,
    appliedCoupons,
    setAppliedCoupons,
    subTotalAmount,
    taxAmountTotal,
    consumptionTaxAmountTotal,
  });

  useEffect(() => {
    dispatch(setFooterBottomSpacing("3.5rem"));
    return () => {
      dispatch(setFooterBottomSpacing(undefined));
    };
  }, []);

  const StyledArrowForwardIcon = styled(ArrowForwardIcon)`
    color: #ffffff;
    margin: 0 auto;
  `;

  const DiscountButton = withStyles((theme) => ({
    root: {
      borderRadius: "0px",
      display: "block !important",
      height: "35.2px",
      color: "#FFFFFF",
      left: "-4px",
      background: homeState.color,
      borderTopLeftRadius: "0",
      borderTopRightRadius: "10px",
      borderBottomLeftRadius: "0",
      borderBottomRightRadius: "10px",

      ".MuiSvgIcon-root": {
        margin: "0 auto",
        color: "#FFFFFF",
      },

      "&:hover": {
        background: "#FFFFFF",
        border: `1px solid ${homeState.color}`,
        "& .MuiSvgIcon-root ": {
          color: `${homeState.color} !important`,
        },
      },
    },
    label: {
      margin: 0,
    },
  }))(Button);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <CartTotalSummaryContainer
      style={{
        display: windowWidth > 768 || showDetailOnMobile ? "flex" : "none",
      }}
    >
      <CartTotalSummaryBody position="block">
        <ContainerTwoStep>
          <CartSummaryModalMobile show={showDetailOnMobile}>
            <>
              <CartTotalSummaryTitleContainer>
                <CartProductSummary
                  data={data}
                  color={color}
                  loading={loading}
                  totalAmount={
                    subTotalAmount + taxAmountTotal + consumptionTaxAmountTotal
                  }
                  appliedCoupons={appliedCoupons}
                />
              </CartTotalSummaryTitleContainer>
            </>

            <CartDetailSummaryContainer>
              <DiscountCodeContainer>
                {/* Discount code input */}
                <DiscountCodeInputContainer>
                  <DiscountCodeInputField
                    color={homeState.color}
                    error={couponErrorMessages.length > 0}
                    id="discount-code-input"
                    type="text"
                    placeholder={
                      language[homeState.default_language]["codeDiscount"]
                    }
                    value={couponCode}
                    onChange={handleInputChange}
                  />
                  <DiscountButton
                    id="discount-code-button"
                    onClick={handleApplyCoupon}
                    variant="contained"
                    disableElevation
                  >
                    <StyledArrowForwardIcon />
                  </DiscountButton>
                </DiscountCodeInputContainer>

                <DiscountCouponsContainer>
                  {appliedCoupons.length > 0 && (
                    <div className="discount_labels">
                      {appliedCoupons.map((coupon, index) => (
                        <DiscountCodeLabelContainer
                          key={index}
                          color={homeState.color}
                          className={
                            showCouponError && coupon.error ? "error" : ""
                          }
                        >
                          <Label
                            className={`discount-code__label-icon ${
                              showCouponError && coupon.error ? "error" : ""
                            }`}
                          />
                          <span
                            className={`discount-code__name ${
                              showCouponError && coupon.error ? "error" : ""
                            }`}
                          >
                            {coupon.code}
                          </span>
                          <span
                            className={`discount-code__discount-amount ${
                              showCouponError && coupon.error ? "error" : ""
                            }`}
                          >
                            (
                            {isFreeShippingCoupon(coupon) ? (
                              language[homeState.default_language]["freeShipping"]
                            ) : coupon.type !== "monto fijo" ? (
                              `${coupon.value}%`
                            ) : (
                              <CurrencyFormat amount={coupon.value} />
                            )}
                            )
                          </span>
                          <DiscountCodeButton
                            className={
                              showCouponError && coupon.error ? "error" : ""
                            }
                            color={homeState.color}
                            onClick={() => handleCloseCoupon(index)}
                          >
                            X
                          </DiscountCodeButton>
                        </DiscountCodeLabelContainer>
                      ))}
                    </div>
                  )}

                  <div>
                    {/* Discount code error */}
                    {couponErrorMessages.length > 0 && (
                      <div className="error-messages">
                        {couponErrorMessages.map((errorMessage, index) => (
                          <DiscountCodeErrorText key={index}>
                            {errorMessage}
                          </DiscountCodeErrorText>
                        ))}
                      </div>
                    )}
                  </div>
                </DiscountCouponsContainer>
              </DiscountCodeContainer>
              {loadCode ? (
                <LoadContainer>
                  <Spinner color={homeState.color} />
                </LoadContainer>
              ) : (
                <div>
                  <CartTotalSummaryTitleContainer>
                    <CartTotalSummaryTitle mp="10px">
                      {language[homeState.default_language]["summary"]}
                    </CartTotalSummaryTitle>
                  </CartTotalSummaryTitleContainer>
                  <CartTotalSummaryDetail
                    style={{ marginBottom: freeShippingNotice ? "1rem" : "2rem" }}
                  >
                    {taxAmountTotal + consumptionTaxAmountTotal === 0 && (
                      <div className="cart-total-summary__title-container">
                        <span className="cart-total-summary__title">
                          {language[homeState.default_language]["subtotal"]}
                        </span>
                        <span className="cart-total-summary__title">
                          <CurrencyFormat amount={Math.abs(subTotalAmount)} />
                        </span>
                      </div>
                    )}
                    {taxAmountTotal + consumptionTaxAmountTotal !== 0 && (
                      <div className="cart-total-summary__title-container">
                        <span className="cart-total-summary__title">
                          {language[homeState.default_language]["subtotal"]} +{" "}
                          {language[homeState.default_language]["tax2"]}
                        </span>
                        <span className="cart-total-summary__title">
                          <CurrencyFormat
                            amount={Math.abs(
                              subTotalAmount +
                                taxAmountTotal +
                                consumptionTaxAmountTotal
                            )}
                          />
                        </span>
                      </div>
                    )}
                    {viewTotalDiscountAmount(
                      items,
                      appliedCoupons,
                      subTotalAmount +
                        taxAmountTotal +
                        consumptionTaxAmountTotal,
                      "amount",
                      "COP",
                      homeState.default_language
                    ) > 0 && (
                      <div className="cart-total-summary__title-container">
                        <span className="cart-total-summary__title">
                          {language[homeState.default_language]["discount"]}
                        </span>
                        <span
                          className="cart-total-summary__title"
                          style={{ color: "#67C940" }}
                        >
                          -{" "}
                          <CurrencyFormat
                            amount={viewTotalDiscountAmount(
                              items,
                              appliedCoupons,
                              subTotalAmount +
                                taxAmountTotal +
                                consumptionTaxAmountTotal,
                              "amount",
                              "COP",
                              homeState.default_language
                            )}
                          />
                        </span>
                      </div>
                    )}
                    <div className="cart-total-summary__title-container">
                      <span className="cart-total-summary__title">
                        {language[homeState.default_language]["send"]}
                      </span>
                      <span className="cart-total-summary__title-detail">
                        {freeShippingReached
                          ? language[homeState.default_language]["free"]
                          : language[homeState.default_language]["valueNextStep"]}
                      </span>
                    </div>
                  </CartTotalSummaryDetail>
                  {freeShippingNotice}
                  <CartTotalSummaryAmount>
                    <div id="title">
                      {language[homeState.default_language]["total"]}
                    </div>
                    <div id="amount" style={{ color: `${homeState.color}` }}>
                      <CurrencyFormat
                        amount={
                          subTotalAmount +
                          taxAmountTotal +
                          consumptionTaxAmountTotal -
                          parseFloat(
                            viewTotalDiscountAmount(
                              items,
                              appliedCoupons,
                              subTotalAmount +
                                taxAmountTotal +
                                consumptionTaxAmountTotal,
                              "amount",
                              "COP",
                              homeState.default_language
                            ).toString()
                          )
                        }
                      />
                    </div>
                  </CartTotalSummaryAmount>
                </div>
              )}
            </CartDetailSummaryContainer>
          </CartSummaryModalMobile>
        </ContainerTwoStep>
      </CartTotalSummaryBody>
    </CartTotalSummaryContainer>
  );
};

export default CartTotalSummary;
